import type { State } from './reducer'
import type { ActionDashboardDataResponse } from '../dashboard'
import type {
    ActionPrivilegesDataResponse,
    ActionAddCouponSuccess
} from '../privileges'
import type {
    ActionHomePageDataResponse,
    ActionRootPageResponse
} from 'shared/store/homepage'
import type {
    ActionAPIAuthError,
    ActionAPIError,
    ActionNetworkError,
    ActionResetNetworkError
} from 'shared/store/actions'
import { fetchPageData } from 'shared/store/actions'
import type { KingdomPoints } from 'shared/types/api'
import endpoints from 'shared/store/endpoints'
import type { Dispatch } from 'redux'
import { ws } from 'shared/store/ws'
import { errors } from 'shared/utils'
import type { TransformForm } from './types'

type ActionPointsDataResponse = {
    type: 'PAGE_DATA_POINTS_RESPONSE',
    data: State
}
export type ActionTransformPointsSuccess = {
    type: 'TRANSFORM_POINTS_SUCCESS',
    pointInfos: KingdomPoints
}
type ActionTransformPointsError = {
    type: 'TRANSFORM_POINTS_ERROR',
    transformPointsError: string
}
export type ActionUntransformPointsSuccess = {
    type: 'UNTRANSFORM_POINTS_SUCCESS',
    pointInfos: KingdomPoints,
    couponCode: string
}
type ActionResetTransformPoints = {
    type: 'RESET_TRANSFORM_POINTS'
}
type ActionPointsLogout = {
    type: 'LOGOUT'
}
type ActionUpdatePoints = {
    type: 'UPDATE_POINTS',
    pointInfos: KingdomPoints
}

const fetchPointsPageData = () =>
    fetchPageData(endpoints.kingdom.points.page, 'PAGE_DATA_POINTS_RESPONSE')

const resetTransformPoints = () => ({
    type: 'RESET_TRANSFORM_POINTS'
})

const updatePoints = (pointInfos: KingdomPoints): ActionUpdatePoints => ({
    type: 'UPDATE_POINTS',
    pointInfos
})

const transformPoints = (form: TransformForm) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const response = await ws.post(
                endpoints.kingdom.points.transform,
                form
            )
            dispatch(actionTransformPointsSuccess(response.data.pointInfos))
            return response.data
        } catch (error) {
            const { response } = error
            console.error('Error transforming points', response)
            let message = 'form.error.default'
            if (response) {
                const { data } = response
                if (data) {
                    switch (data.message) {
                        case errors.ACTION_LOCKED:
                            message = 'kingdom.points.transform.locked'
                            break
                        case errors.LACK_POINTS:
                            message = 'kingdom.points.transform.lack'
                            break
                        case errors.PROMO_NOT_FOUND:
                            message = 'kingdom.points.transform.not.found'
                            break
                        default:
                            break
                    }
                }
            }
            dispatch(actionTransformPointsError(message))
            return false
        }
    }
}

const untransformPoints = (couponCode: string) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const response = await ws.get(
                `${endpoints.kingdom.points.untransform}`,
                {
                    params: { uniqueId: couponCode }
                }
            )
            dispatch(actionUntransformPointsSuccess(response.data, couponCode))
        } catch (error) {
            const { response } = error
            console.error('Error untransforming points', response)
        }
    }
}

const renewPoints = () => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const response = await ws.get(endpoints.kingdom.points.renew)
            dispatch(updatePoints(response.data))
            return true
        } catch (error) {
            console.error('Error when renewing points', error)
            return false
        }
    }
}

const actionTransformPointsSuccess = (
    pointInfos: KingdomPoints
): ActionTransformPointsSuccess => ({
    type: 'TRANSFORM_POINTS_SUCCESS',
    pointInfos
})

const actionTransformPointsError = (
    transformPointsError: string
): ActionTransformPointsError => ({
    type: 'TRANSFORM_POINTS_ERROR',
    transformPointsError
})

const actionUntransformPointsSuccess = (
    pointInfos: KingdomPoints,
    couponCode: string
): ActionUntransformPointsSuccess => ({
    type: 'UNTRANSFORM_POINTS_SUCCESS',
    pointInfos,
    couponCode
})

export {
    fetchPointsPageData,
    renewPoints,
    resetTransformPoints,
    transformPoints,
    untransformPoints,
    updatePoints
}

export type Action =
    | ActionPointsDataResponse
    | ActionDashboardDataResponse
    | ActionPrivilegesDataResponse
    | ActionAPIAuthError
    | ActionAPIError
    | ActionNetworkError
    | ActionResetNetworkError
    | ActionTransformPointsSuccess
    | ActionTransformPointsError
    | ActionUntransformPointsSuccess
    | ActionResetTransformPoints
    | ActionPointsLogout
    | ActionHomePageDataResponse
    | ActionRootPageResponse
    | ActionUpdatePoints
    | ActionAddCouponSuccess
